import { useEffect, useState, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  SignUpState,
  selectMentor,
  selectLicenseCount,
  selectSessionId,
  selectThinkers,
  setMentor,
} from '../../features/signUp/signUpSlice';
import { store, useAppSelector } from '../../stores/AppStore';
import {
  signupDevices,
  signupGoals,
  signupReferralTypes,
} from '../../constants';
import { useIndividualThinkerSignupMutation } from '../../services/apiSlice';
import { handleError } from '../../services/ErrorService';
import ProgressBar from './ProgressBar';
import Option from './Option';
import useSessionStatusCheck from '../../hooks/useSessionStatusCheck';

interface MentorDetailsProperties {
  onNextStep: () => void;
  onPreviousStep: () => void;
}

const SignUpQuestionnaire = ({
  onNextStep,
  onPreviousStep,
}: MentorDetailsProperties) => {
  const mentor = useAppSelector(selectMentor);
  const licenseCount = useAppSelector(selectLicenseCount);
  const sessionId = useAppSelector(selectSessionId);
  const thinkers = useAppSelector(selectThinkers);
  const { validateAndProceed, validateOnMount } = useSessionStatusCheck();

  const { control, getValues, handleSubmit } = useForm<SignUpState['mentor']>({
    mode: 'all',
    defaultValues: mentor,
  });

  const [createThinkers, { isLoading: isCreatingThinkers }] =
    useIndividualThinkerSignupMutation();
  const [accountsCreated, setAccountsCreated] = useState(false);
  const [creationError, setCreationError] = useState<string | undefined>(
    undefined,
  );
  const [sessionValidated, setSessionValidated] = useState(false);

  // Validate session status on mount before creating thinkers
  const initThinkerAccounts = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    try {
      await createThinkers({
        sessionId,
        mentor,
        thinkers,
      }).unwrap();

      setAccountsCreated(true);
      setCreationError(undefined);
      toast.success('Your accounts are ready!');
    } catch (error: any) {
      // Check if it's an "already created" error (idempotent success)
      if (
        error?.status === 409 ||
        error?.data?.message?.includes('already exists')
      ) {
        setAccountsCreated(true);
        setCreationError(undefined);
        return;
      }

      // Real error - report to Sentry and show to user
      const errorMessage =
        error?.data?.message ||
        'Account setup failed. Please try again or contact support.';
      setCreationError(errorMessage);
      handleError(error, {
        displayedError:
          'Account setup had an issue. Our team will help resolve this.',
        tags: {
          component: 'Questionnaire',
          operation: 'createThinkers',
        },
      });
    }
  }, [sessionId, mentor, thinkers, createThinkers]);

  // Validate session on mount, then create thinker accounts
  useEffect(() => {
    const validateAndInit = async () => {
      const isValid = await validateOnMount();
      setSessionValidated(true);
      if (isValid) {
        initThinkerAccounts();
      }
    };
    validateAndInit();
  }, [validateOnMount, initThinkerAccounts]);

  const handleContinue = async () => {
    // Don't allow advancing if account creation failed
    if (creationError) {
      toast.error(
        'Please wait for account creation to complete or contact support if the issue persists.',
      );
      return;
    }

    if (!accountsCreated && isCreatingThinkers) {
      toast('Please wait while we set up your accounts...');
      return;
    }

    // Validate session status before proceeding
    validateAndProceed(() => {
      store.dispatch(setMentor(getValues()));
      onNextStep();
    });
  };

  // Show loading state while validating session or creating thinker accounts
  if (!sessionValidated || isCreatingThinkers) {
    return (
      <div className="flex flex-col gap-8 justify-center items-center h-full w-full">
        <img src="/images/loading.svg" alt="Loading" />
        <p className="mb-4 text-center font-medium">
          {sessionValidated
            ? 'Setting up your thinker accounts...'
            : 'Validating your session...'}
        </p>
        <p className="text-sm text-gray-500 text-center">
          This will only take a moment
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="mentor-details"
      onSubmit={handleSubmit(handleContinue)}
      className="flex flex-col gap-2 md:gap-4 w-full h-full"
    >
      <div className="flex flex-col gap-2 md:gap-4 w-full md:w-[70%] lg:w-[60%] mx-auto">
        <h1 className="text-3xl md:text-4xl font-medium w-full">
          Optional Questions
        </h1>
        <p className="text-gray-500 text-sm md:text-base">
          Answering these questions will help us better serve you, but there's
          no pressure — your experience with Fill the Math GAPS will be great
          either way.
        </p>
        <div className="flex flex-col gap-2 md:gap-4 w-full pb-4 border-b-2 border-gray-200">
          <label
            htmlFor="numberOfChildren"
            className="text-sm md:text-base font-medium "
          >
            Number of children in your household
          </label>
          <Controller
            render={({ field }) => (
              <div className="flex gap-2">
                {Array.from({ length: 5 }, (_, index) => (
                  <Option
                    key={`children-${index + 1}`}
                    dataTestId={`children-${index + 1}`}
                    value={index + 1}
                    onClick={() =>
                      field.onChange(String(index === 4 ? '5+' : index + 1))
                    }
                    isSelected={
                      field.value === String(index === 4 ? '5+' : index + 1)
                    }
                    isRadio={false}
                  >
                    <p className="mx-1.5 md:-mx-0.5 md:-my-2">
                      {index === 4 ? '5+' : index + 1}
                    </p>
                  </Option>
                ))}
              </div>
            )}
            name="numberOfChildren"
            control={control}
          />
        </div>
        <div className="flex flex-col gap-2 md:gap-4 w-full pb-4 border-b-2 border-gray-200">
          <label htmlFor="goal" className="text-sm md:text-base font-medium">
            What is your primary goal for joining our program?
          </label>
          <Controller
            render={({ field }) => (
              <div className="flex gap-2 flex-wrap">
                {signupGoals.map((goal, index) => (
                  <Option
                    key={`goal-${index + 1}`}
                    dataTestId={`goal-${index + 1}`}
                    value={index + 1}
                    onClick={() => field.onChange(goal)}
                    isSelected={field.value === goal}
                    isRadio={false}
                  >
                    <p className="mx-1.5 md:-mx-0.5 md:-my-2 whitespace-nowrap">
                      {goal}
                    </p>
                  </Option>
                ))}
              </div>
            )}
            name="goal"
            control={control}
          />
        </div>
        <div className="flex flex-col gap-2 md:gap-4 w-full pb-4 border-b-2 border-gray-200">
          <label
            htmlFor="preferredDevice"
            className="text-sm md:text-base font-medium"
          >
            What device will your {licenseCount === 1 ? 'Thinker' : 'Thinkers'}{' '}
            primarily use to access our services?
          </label>
          <Controller
            render={({ field }) => (
              <div className="flex gap-2 flex-wrap">
                {signupDevices.map((device, index) => (
                  <Option
                    key={`device-${index + 1}`}
                    dataTestId={`device-${index + 1}`}
                    value={index + 1}
                    onClick={() => field.onChange(device)}
                    isSelected={field.value === device}
                    isRadio={false}
                  >
                    <p className="mx-1.5 md:-mx-0.5 md:-my-2 whitespace-nowrap">
                      {device}
                    </p>
                  </Option>
                ))}
              </div>
            )}
            name="preferredDevice"
            control={control}
          />
        </div>
        <div className="flex flex-col gap-2 md:gap-4 w-full">
          <label
            htmlFor="referral"
            className="text-sm md:text-base font-medium"
          >
            Who referred you to our service?
          </label>
          <Controller
            render={({ field }) => (
              <div className="flex gap-2 flex-wrap">
                {signupReferralTypes.map((referral, index) => (
                  <Option
                    key={`referral-${index + 1}`}
                    dataTestId={`referral-${index + 1}`}
                    value={index + 1}
                    onClick={() => field.onChange(referral)}
                    isSelected={field.value === referral}
                    isRadio={false}
                  >
                    <p className="mx-1.5 md:-mx-0.5 md:-my-2 whitespace-nowrap">
                      {referral}
                    </p>
                  </Option>
                ))}
              </div>
            )}
            name="referral"
            control={control}
          />
        </div>
      </div>

      <ProgressBar />

      {/* Action Buttons */}
      <div className="flex gap-2 pt-4 md:pt-6 w-full justify-between items-center">
        <button
          data-testid={`back-button`}
          type="button"
          className="
            px-10 py-2 w-max rounded-full border font-medium
            transition-all duration-300 ease-in-out
            hover:shadow hover:outline hover:outline-tttDefault hover:outline-[1px]
            disabled:opacity-50 disabled:cursor-not-allowed
          "
          onClick={onPreviousStep}
        >
          Back
        </button>
        <button
          data-testid={`continue-button`}
          type="submit"
          className="
            px-10 py-2 w-max rounded-full
            font-medium text-white bg-tttDefault
            transition-all duration-300 ease-in-out
            hover:shadow hover:bg-opacity-90
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          Finish
        </button>
      </div>
    </form>
  );
};

export default SignUpQuestionnaire;
